import { apiClient } from './client';
import { WorkspaceFile } from '../types';

interface FileReadResult {
  content: string;
  file: WorkspaceFile;
}

interface FileWriteResult {
  path: string;
  name: string;
  size?: number;
  cdnUrl: string;
}

interface CopyResult {
  sourcePath: string;
  destPath: string;
  name: string;
  cdnUrl: string;
}

interface SearchResult {
  path: string;
  name: string;
  cdnUrl: string;
}

interface ContentSearchResult {
  path: string;
  line: number;
  content: string;
  cdnUrl: string;
}

// List files in a directory
export async function listFiles(
  workspace: string,
  path: string = '/'
): Promise<WorkspaceFile[]> {
  const query = path !== '/' ? `?path=${encodeURIComponent(path)}` : '';
  return apiClient<WorkspaceFile[]>(
    `/files/${encodeURIComponent(workspace)}${query}`
  );
}

// Read file content (alias: getFile for backwards compatibility)
export async function readFile(
  workspace: string,
  filePath: string
): Promise<FileReadResult> {
  return apiClient<FileReadResult>(
    `/files/${encodeURIComponent(workspace)}/file?path=${encodeURIComponent(filePath)}`
  );
}

// Backwards compatible aliases
export const getFile = readFile;
export const getFileContent = readFile;

// Get file info/metadata
export async function getFileInfo(
  workspace: string,
  filePath: string
): Promise<WorkspaceFile> {
  return apiClient<WorkspaceFile>(
    `/files/${encodeURIComponent(workspace)}/info?path=${encodeURIComponent(filePath)}`
  );
}

// Check if file exists
export async function fileExists(
  workspace: string,
  filePath: string
): Promise<boolean> {
  const result = await apiClient<{ exists: boolean }>(
    `/files/${encodeURIComponent(workspace)}/exists?path=${encodeURIComponent(filePath)}`
  );
  return result.exists;
}

// Search files by pattern (glob)
export async function searchFiles(
  workspace: string,
  pattern: string
): Promise<SearchResult[]> {
  return apiClient<SearchResult[]>(
    `/files/${encodeURIComponent(workspace)}/search?pattern=${encodeURIComponent(pattern)}`
  );
}

// Search file contents (grep)
export async function searchContent(
  workspace: string,
  pattern: string,
  searchPath: string = '/'
): Promise<ContentSearchResult[]> {
  const pathQuery = searchPath !== '/' ? `&path=${encodeURIComponent(searchPath)}` : '';
  return apiClient<ContentSearchResult[]>(
    `/files/${encodeURIComponent(workspace)}/search/content?pattern=${encodeURIComponent(pattern)}${pathQuery}`
  );
}

// Write file content
export async function writeFile(
  workspace: string,
  filePath: string,
  content: string
): Promise<FileWriteResult> {
  return apiClient<FileWriteResult>(
    `/files/${encodeURIComponent(workspace)}/file`,
    {
      method: 'POST',
      body: { path: filePath, content },
    }
  );
}

// Append content to file
export async function appendFile(
  workspace: string,
  filePath: string,
  content: string
): Promise<FileWriteResult> {
  return apiClient<FileWriteResult>(
    `/files/${encodeURIComponent(workspace)}/append`,
    {
      method: 'POST',
      body: { path: filePath, content },
    }
  );
}

// Create directory
export async function createDirectory(
  workspace: string,
  dirPath: string
): Promise<{ path: string; name: string; type: 'directory' }> {
  return apiClient(
    `/files/${encodeURIComponent(workspace)}/directory`,
    {
      method: 'POST',
      body: { path: dirPath },
    }
  );
}

// Copy file
export async function copyFile(
  workspace: string,
  sourcePath: string,
  destPath: string
): Promise<CopyResult> {
  return apiClient<CopyResult>(
    `/files/${encodeURIComponent(workspace)}/copy`,
    {
      method: 'POST',
      body: { sourcePath, destPath },
    }
  );
}

// Move/rename file
export async function moveFile(
  workspace: string,
  path: string,
  newPath: string
): Promise<FileWriteResult> {
  return apiClient<FileWriteResult>(
    `/files/${encodeURIComponent(workspace)}/file`,
    {
      method: 'PATCH',
      body: { path, newPath },
    }
  );
}

// Delete file
export async function deleteFile(
  workspace: string,
  filePath: string
): Promise<void> {
  return apiClient(
    `/files/${encodeURIComponent(workspace)}/file?path=${encodeURIComponent(filePath)}`,
    { method: 'DELETE' }
  );
}

// Get CDN URL (client-side computation, no API call)
export function getCdnUrl(workspace: string, path: string): string {
  const cdnBase = import.meta.env.VITE_CDN_BASE || 'https://cdn.getsmartup.ai/workspaces';
  return `${cdnBase}/${workspace}${path.startsWith('/') ? path : '/' + path}`;
}
